import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import LiveTimer from './LiveTimer'
import TimeEntryForm from './TimeEntryForm'
import CustomDropdown from './CustomDropdown'
import { UpdateTimeEntryInput } from '@/services/timeTracking'
import { Project, TimeEntry, UpdateTimeEntryRequest } from '../../../shared/types'
import { getEffectiveTimezone } from '@/utils/timezoneOptions'

// Service TimeEntry interface (without isRunning property)
interface ServiceTimeEntry {
  id: string;
  userId: string;
  projectId?: string;
  description: string;
  startTime: string;
  endTime?: string;
  duration?: number;
  tags?: string[];
  location?: string;
  timezone?: string; // IANA timezone identifier (e.g., "Europe/Berlin")
  createdAt: string;
  updatedAt: string;
}

// Helper function to convert service TimeEntry to shared TimeEntry
const convertToSharedTimeEntry = (entry: ServiceTimeEntry): TimeEntry => ({
  ...entry,
  isRunning: !entry.endTime
})

interface TimeTrackerProps {
  runningEntry: ServiceTimeEntry | null
  startTimeEntry: (input: { description: string; projectId?: string; location?: string; startTime: string; timezone?: string }) => Promise<{ timeEntry: ServiceTimeEntry; message: string }>
  stopTimeEntry: (id: string) => Promise<{ timeEntry: ServiceTimeEntry; message: string }>
  updateTimeEntry: (id: string, updates: UpdateTimeEntryInput) => Promise<{ timeEntry: ServiceTimeEntry; message: string }>
  formatDuration: (seconds: number) => string
  getCurrentDuration: (entry: ServiceTimeEntry) => number
  projects: Project[]
  preferredTimezone?: string | null // User's preferred timezone setting (null = use browser)
  weekStartsOn?: 0 | 1 // 0 = Sunday, 1 = Monday (default)
  showActiveEntryAnimations?: boolean // Show pulsing effects when a time entry is running (default: true)
}

export default function TimeTracker({
  runningEntry,
  startTimeEntry,
  stopTimeEntry,
  updateTimeEntry,
  formatDuration,
  getCurrentDuration,
  projects,
  preferredTimezone,
  weekStartsOn = 1,
  showActiveEntryAnimations = true
}: TimeTrackerProps) {
  // Get effective timezone (user preference or browser default)
  const effectiveTimezone = getEffectiveTimezone(preferredTimezone);
  const [editing, setEditing] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [description, setDescription] = useState('Start')
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [selectedLocation, setSelectedLocation] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)



  const handleStop = async () => {
    if (!runningEntry) return

    try {
      await stopTimeEntry(runningEntry.id)
    } catch {
      // Error is handled by the hook
    }
  }

  const formatStartTime = (startTime: string) => {
    return new Date(startTime).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
  }

  const handleUpdate = async (updates: UpdateTimeEntryRequest) => {
    if (!runningEntry) return
    try {
      await updateTimeEntry(runningEntry.id, updates)
      setEditing(false)
    } catch {
      // Error is handled by the hook
    }
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setDescription(value)
    
    // Auto-resize the textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      const lineHeight = 20 // Approximate line height in pixels
      const maxHeight = lineHeight * 8 // Maximum 8 lines
      const newHeight = Math.min(scrollHeight, maxHeight)
      textareaRef.current.style.height = `${newHeight}px`
    }
  }

  // Effect to handle initial resize
  useEffect(() => {
    if (textareaRef.current && description) {
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      const lineHeight = 20
      const maxHeight = lineHeight * 8
      const newHeight = Math.min(scrollHeight, maxHeight)
      textareaRef.current.style.height = `${newHeight}px`
    }
  }, [description])

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-300">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900">Time Tracker</h2>
      </div>
      
      {runningEntry ? (
        <div className="space-y-4">
          <div
            className="relative overflow-hidden bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
            onClick={(e) => {
              // Only open edit if not clicking the Stop button
              if (!(e.target as HTMLElement).closest('button')) {
                setEditing(true)
              }
            }}
          >
            {/* Animated background pulse */}
            <div className={`absolute inset-0 bg-green-400 opacity-0 rounded-xl ${showActiveEntryAnimations ? 'animate-pulse' : ''}`}></div>

            <div className="relative">
              <div className="flex items-center space-x-2 mb-4">
                <div className={`w-2 h-2 bg-green-500 rounded-full ${showActiveEntryAnimations ? 'animate-pulse' : ''}`}></div>
                <h3 className="font-medium text-green-900">Currently Tracking</h3>
              </div>
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <p className="text-green-800 font-medium text-lg truncate">{runningEntry.description}</p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <LiveTimer 
                    entry={runningEntry}
                    formatDuration={formatDuration}
                    getCurrentDuration={getCurrentDuration}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-sm text-green-700 min-w-0 flex-1">
                  <div className="flex items-center space-x-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Started at {formatStartTime(runningEntry.startTime)}</span>
                  </div>
                  {runningEntry.projectId && (
                    <div className="flex items-center space-x-1 min-w-0 flex-1">
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ 
                          backgroundColor: projects.find(p => p.id === runningEntry.projectId)?.color || '#6B7280' 
                        }}
                      />
                      <span className="truncate">{projects.find(p => p.id === runningEntry.projectId)?.name || 'Unknown Project'}</span>
                    </div>
                  )}
                  {runningEntry.location && (
                    <div className="flex items-center space-x-1 min-w-0 flex-1">
                      <span className="flex-shrink-0">🏢</span>
                      <span className="truncate">{runningEntry.location}</span>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStop();
                  }}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200 font-medium"
                >
                  <div className="flex items-center space-x-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                    </svg>
                    <span>Stop</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <form onSubmit={async (e) => {
            e.preventDefault()
            if (!description.trim()) return

            try {
              setIsStarting(true)
              await startTimeEntry({
                description: description.trim(),
                projectId: selectedProjectId || undefined,
                location: selectedLocation || undefined,
                startTime: new Date().toISOString(),
                timezone: effectiveTimezone
              })
              setDescription('')
              setSelectedProjectId('')
              setSelectedLocation('')
            } catch {
              // Error is handled by the hook
            } finally {
              setIsStarting(false)
            }
          }}>
            <div className="space-y-4">
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  What are you working on?
                </label>
                <textarea
                  ref={textareaRef}
                  id="description"
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Enter a description..."
                  rows={1}
                  maxLength={500}
                  className="w-full min-h-[42px] p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden transition-height duration-200 text-sm text-gray-700 placeholder:text-gray-500"
                  required
                />
              </div>
              
              <CustomDropdown
                id="timer-project"
                label="Project"
                placeholder="Select a project"
                options={projects.map(project => ({ value: project.id, label: project.name }))}
                selectedValues={selectedProjectId ? [selectedProjectId] : []}
                onSelectionChange={(values) => {
                  const newProjectId = values[0] || ''
                  setSelectedProjectId(newProjectId)
                  // Clear location if project is cleared
                  if (!newProjectId) {
                    setSelectedLocation('')
                  } else {
                    // Auto-select first location if project has locations
                    const selectedProject = projects.find(p => p.id === newProjectId)
                    if (selectedProject && selectedProject.locations.length > 0) {
                      setSelectedLocation(selectedProject.locations[0].name)
                    } else {
                      setSelectedLocation('')
                    }
                  }
                }}
                multiSelect={false}
                transparency="low"
              />

              <CustomDropdown
                id="timer-location"
                label={(() => {
                  const selectedProject = projects.find(p => p.id === selectedProjectId)
                  const hasLocations = selectedProject && selectedProject.locations.length > 0
                  return hasLocations ? "Location *" : "Location"
                })()}
                placeholder={(() => {
                  if (!selectedProjectId) return "Select a project first"
                  const selectedProject = projects.find(p => p.id === selectedProjectId)
                  if (!selectedProject || selectedProject.locations.length === 0) return "No locations available"
                  return "Select a location"
                })()}
                options={(() => {
                  const selectedProject = projects.find(p => p.id === selectedProjectId)
                  if (!selectedProject) return []
                  return selectedProject.locations.map((location: { name: string }) => ({ value: location.name, label: location.name }))
                })()}
                selectedValues={selectedLocation ? [selectedLocation] : []}
                onSelectionChange={(values) => setSelectedLocation(values[0] || '')}
                multiSelect={false}
                disabled={!selectedProjectId}
                transparency="low"
              />
              
              <button
                type="submit"
                disabled={isStarting || !description.trim()}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isStarting ? (
                  'Starting...'
                ) : (
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>
            </div>
          </form>
        </div>
      )}



      {editing && runningEntry && createPortal(
        <TimeEntryForm
          mode="edit"
          entry={convertToSharedTimeEntry(runningEntry)}
          projects={projects}
          onSubmit={handleUpdate}
          onCancel={() => setEditing(false)}
          loading={false}
          preferredTimezone={preferredTimezone}
          weekStartsOn={weekStartsOn}
        />,
        document.body
      )}
    </div>
  )
} 